import api, { handleResponse, handleError } from './api';

/**
 * Admin newsletter composition and broadcast. Admin-only server-side.
 *
 * The send is deliberately two steps: `sendTest` mails the admin a copy and
 * unlocks `send`, which only queues the broadcast — a background dispatcher
 * delivers it in batches. `get` returns the server's own Before-Send verdict
 * (`canSend` + `problems`) so the UI never has to re-implement those rules.
 */
const newsletterService = {
  async list() {
    try {
      const r = await api.get('/newsletters');
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  async get(id) {
    try {
      const r = await api.get(`/newsletters/${id}`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /** Creates when `payload.id` is absent, updates when present. */
  async save(payload) {
    try {
      const r = await api.post('/newsletters', payload);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  async remove(id) {
    try {
      const r = await api.delete(`/newsletters/${id}`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /** Mails the signed-in admin a copy. Required before `send` unlocks. */
  async sendTest(id) {
    try {
      const r = await api.post(`/newsletters/${id}/test`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /** Queues the broadcast. Returns how many recipients were queued. */
  async send(id) {
    try {
      const r = await api.post(`/newsletters/${id}/send`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * Arms a future send. `scheduledFor` is an ISO-8601 UTC instant. The
   * newsletter stays editable until it fires — but editing invalidates the
   * test send, and the dispatcher re-checks at fire time.
   */
  async schedule(id, scheduledForIsoUtc) {
    try {
      const r = await api.post(`/newsletters/${id}/schedule`, { scheduledFor: scheduledForIsoUtc });
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  async unschedule(id) {
    try {
      const r = await api.post(`/newsletters/${id}/unschedule`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * Copies a newsletter into a fresh draft. This is how you revise one that
   * already went out — email cannot be recalled.
   */
  async duplicate(id) {
    try {
      const r = await api.post(`/newsletters/${id}/duplicate`);
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * Uploads a section image and returns { path, url }. Use `url` — email
   * clients need an absolute, permanent address.
   */
  async uploadSectionImage(file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/newsletters/section-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },
  // ── Email Studio ──
  /** { count, optedOut } for { audienceKind: everyone|city|hosts|test, audience: { regionIds } }. */
  async countAudience(payload) {
    try { return handleResponse(await api.post('/newsletters/audience/count', payload)); } catch (e) { return handleError(e); }
  },
  /** BY CITY chips: [{ id, name, count }]. */
  async listCities() {
    try { return handleResponse(await api.get('/newsletters/cities')); } catch (e) { return handleError(e); }
  },
  /** EVENT block picker: upcoming public events. */
  async listEvents(q) {
    try { return handleResponse(await api.get('/newsletters/events', { params: q ? { q } : {} })); } catch (e) { return handleError(e); }
  },
  async listTemplates() {
    try { return handleResponse(await api.get('/newsletters/templates')); } catch (e) { return handleError(e); }
  },
  async saveTemplate(tpl) {
    try { return handleResponse(await api.post('/newsletters/templates', tpl)); } catch (e) { return handleError(e); }
  },
  async deleteTemplate(id) {
    try { return handleResponse(await api.delete(`/newsletters/templates/${id}`)); } catch (e) { return handleError(e); }
  },
};

export default newsletterService;
